// @flow
import { bindActionCreators } from "redux";
import { isFunction } from "lodash";

const getActionsSettings = (types: Object, overrides: ?Object = {}) =>
	// $FlowFixMe
	Object.values(types).reduce((res: Object, tVal: string) => {
		res[tVal] = (overrides && overrides[tVal]) || true;
		return res;
	}, {});

// todo: support actions as array and generate symbol for the value and return the key/val map

type BoundActionsWithCreators = {
	(() => Object): Object,
	creators: { [string]: Function },
};

const getBoundActionCreators = (actions): BoundActionsWithCreators => {
	const actionCreators = Object.keys(actions).reduce((res, key) => {
		res[key] = (...args) => (actions[key]._customAC === true ? actions[key](...args) : getSyncActionCreator(key, actions, args));
		return res;
	}, {});

	const bindFn = (dispatch: Function) => bindActionCreators(actionCreators, dispatch);

	bindFn.creators = actionCreators;

	return bindFn;
};

const getSyncActionCreator = (key, actions, args) => ({
	type: key,
	payload: isFunction(actions[key])
		? actions[key](...args) // use the action method to retrieve the payload
		: args[0], // no action method, return the first dispatched arg as the payload object
});

const dispatcher = (dispatch: Function, actions: any[]) => actions.forEach(a => dispatch({ type: a[0], payload: a[1] }));

/**
 * returns a function that can be called either:
 *  a) with type and payload:
 *          const dispatcher = createDispatcher(dispatch);
 *          dispatcher(myType, {});
 *  b) with an array of arrays:
 *          const dispatcher = createDispatcher(dispatch);
 *          dispatcher([[myType, {}], [otherType, {}]]);
 *
 * @param dispatch
 */
export const createDispatcher = (dispatch: Function) => (type: string | any[], payload: ?Object = null) =>
	dispatcher(dispatch, Array.isArray(type) ? type : [[type, payload]]);

export const createCustomCreator = (func: Function) => {
	const actionCreator = (...args: any) => (dispatch: Function, getState: Function) => func(...args, dispatch, getState, createDispatcher(dispatch));

	actionCreator._customAC = true;

	return actionCreator;
};

export const bindStructuredActions = (mapping: Object) => (dispatch: Function): any =>
	Object.keys(mapping).reduce((res, key) => {
		res[key] = mapping[key](dispatch);
		return res;
	}, {});

/**
 * returns a function that can be directly passed to react-redux connect's mapDispatchToProps
 *
 * @param types - is a key/val map, the val(!) will be the name of the action available in the component's props object
 *
 * @param overrides - is a key/val where the key should match one of the val(!) in the types parameter
 * the value can be one of:
 *      a) any value (just not a function) = the dispatched value will be passed as is to the reducer
 *      b) a function = the returned value of this function will be used as the payload,
 *          the dispatched values will be passed as argument to this function
 *      c) the result of createCustomCreator (a function) this will pass the custom creator to the
 *          thunk middleware, the creator will receive the dispatch and getState methods as the first arguments.
 *          the rest of the dispatched values will be provided as the next arguments
 *
 *
 * Usage Example:
 *
 *      createBoundActionCreators({
 *      "TYPE1":"TYPE1".
 *      "TYPE2":"TYPE2-TYPE".
 *      "TYPE3":"TYPE3.3".
 *      },{
 *          "TYPE2-TYPE": (a,b)=>({val: a*b}),
 *          "TYPE3.3": createCustomCreator
 *      })
 */
const createBoundActionCreators = (types: Object, overrides: ?Object) => getBoundActionCreators(getActionsSettings(types, overrides));

/**
 * This method generates request statuses for dispatching to the reducer. the method return the actionType as a string and the statuses as an Object
 * const action = generateApiActionType('IMAGE_PENDING');
 * {
 *   PENDING : 'FETCH_IMAGE_PENDING',
 *   SUCCESS : 'FETCH_IMAGE_SUCCESS',
 *   FAILURE : 'FETCH_IMAGE_FAILURE'
 * }
 * action === 'IMAGE_PENDING' // true
 * @param actionType
 * @returns {String and Object}
 */
// TODO: dont know how to deal with this in flow...
export const generateApiActionType = (actionType: string): any => {
	// eslint-disable-next-line no-new-wrappers
	const action = new String(actionType);

	// $FlowFixMe - flow doesnt like this...
	Object.assign(action, {
		PENDING: `${actionType}_PENDING`,
		SUCCESS: `${actionType}_SUCCESS`,
		FAILURE: `${actionType}_FAILURE`,
	});

	return action;
};

export default createBoundActionCreators;
